import type { Exercise, SlotTag } from '../data/exercise-types';
import { exercisesForSlot, getExercise } from '../data/exercises';
import type { SessionSlot, SessionTemplate } from '../data/splits';
import type { ExercisePrefs, Goal, LoggedExercise, WorkoutSession } from '../store/types';
import type { BlockInfo } from './blocks';
import { suggestLoad } from './progression';

export interface GenerateOptions {
  template: SessionTemplate;
  equipment: Set<string>;
  workouts: WorkoutSession[];
  prefs: ExercisePrefs;
  block: BlockInfo;
  goal: Goal;
  /** Pair accessory slots to run back-to-back. Defaults to on. */
  useSupersets?: boolean;
}

export interface GeneratedWorkout {
  exercises: LoggedExercise[];
  /** Slots we could not fill with your current equipment. */
  unfilled: SlotTag[];
  notes: string[];
}

/** How many past sessions of this template we look at when rotating accessories. */
const ROTATION_LOOKBACK = 3;

/**
 * Picks the exercise for each slot.
 *
 * Two competing needs. Your main lifts should stay the *same* across a block,
 * because "add 2.5 kg to the thing you did last week" only works if it is the
 * same thing. Your accessories should *change*, because that is where variety
 * buys you new stimulus at no cost. So anchor slots lock to whatever you used
 * earlier in the block, and everything else carries a recency penalty that
 * pushes the rotation along on its own.
 *
 * Selection is fully deterministic — no randomness — so opening the app twice
 * on the same morning gives you the same workout instead of reshuffling it.
 */
export function generateWorkout(opts: GenerateOptions): GeneratedWorkout {
  const { template, equipment, workouts, prefs, block, goal, useSupersets = true } = opts;

  const excluded = new Set(prefs.excluded);
  const favorites = new Set(prefs.favorites);
  const finished = workouts.filter((w) => w.finishedAt);

  // Past sessions of this same template, newest first.
  const sameTemplate = [...finished]
    .filter((w) => w.templateId === template.id)
    .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));

  // Sessions from the current block — anchors lock onto these.
  const thisBlock = sameTemplate.filter((w) => w.blockNumber === block.blockNumber);

  const chosen = new Set<string>();
  const exercises: LoggedExercise[] = [];
  const unfilled: SlotTag[] = [];
  const notes: string[] = [];

  const slots = planSlots(template, block, goal, useSupersets);

  for (const slotSpec of slots) {
    const candidates = exercisesForSlot(slotSpec.slot, equipment).filter(
      (e) => !excluded.has(e.id) && !chosen.has(e.id),
    );

    if (candidates.length === 0) {
      unfilled.push(slotSpec.slot);
      continue;
    }

    const pick = slotSpec.anchor
      ? pickAnchor(slotSpec.slot, candidates, thisBlock, favorites, sameTemplate)
      : pickRotating(slotSpec.slot, candidates, sameTemplate, favorites, goal);

    chosen.add(pick.id);

    const sets = resolveSets(slotSpec, pick, block);
    const repRange = slotSpec.repRange ?? pick.repRange;
    const suggestion = suggestLoad(pick, workouts, block.loadMultiplier);

    exercises.push({
      exerciseId: pick.id,
      slot: slotSpec.slot,
      targetSets: sets,
      targetReps: repRange,
      suggestionKg: suggestion.weightKg,
      suggestionReason: suggestion.reason,
      sets: Array.from({ length: sets }, () => ({
        weightKg: suggestion.weightKg ?? 0,
        reps: 0,
        done: false,
      })),
    });
  }

  if (useSupersets) applySupersets(exercises, slots);

  if (block.isDeload) {
    notes.push(
      'Deload week: sets are cut to about 60% and loads to 90%. Keep the technique crisp and leave 3–4 reps in reserve on everything. You are not here to test yourself this week.',
    );
  }
  if (unfilled.length > 0) {
    notes.push(
      `No equipment available for: ${unfilled.join(', ')}. Turn more kit on in Settings → Equipment if the gym has it.`,
    );
  }

  return { exercises, unfilled, notes };
}

/**
 * Expands a template's slot list for this particular week: drops optional work
 * on a deload, and adds a conditioning finisher when you're chasing fat loss.
 */
function planSlots(
  template: SessionTemplate,
  block: BlockInfo,
  goal: Goal,
  useSupersets: boolean,
): SessionSlot[] {
  let slots = [...template.slots];

  if (block.isDeload) {
    slots = slots.filter((s) => !s.optional);
  }

  const hasFinisher = slots.some((s) => s.slot === 'finisher');
  if (goal === 'fat_loss' && !block.isDeload && !hasFinisher) {
    slots.push({
      slot: 'finisher',
      sets: 3,
      optional: true,
      note: 'Added for your fat-loss goal — short, hard, and it does not eat into recovery the way extra lifting would.',
    });
  }

  if (useSupersets) slots = pairAdjacent(slots);

  return slots;
}

/**
 * Moves each superset partner to sit directly after its pair.
 *
 * Templates list slots in the order they read best; a pair can be several
 * entries apart. You can't superset two things that aren't next to each other
 * on the screen, so this pulls them together while leaving everything else
 * where it was.
 */
function pairAdjacent(slots: SessionSlot[]): SessionSlot[] {
  const remaining = [...slots];
  const ordered: SessionSlot[] = [];

  while (remaining.length > 0) {
    const current = remaining.shift()!;
    ordered.push(current);

    if (!current.supersetWith) continue;
    // A partner that was dropped (optional work on a deload) just means no pair.
    const partnerIndex = remaining.findIndex((s) => s.slot === current.supersetWith);
    if (partnerIndex === -1) continue;

    ordered.push(remaining.splice(partnerIndex, 1)[0]!);
  }

  return ordered;
}

/**
 * Tags the generated exercises that belong to a pair. Only pairs that both got
 * filled become a group — if one slot had no available equipment, the other
 * runs on its own as a normal exercise.
 */
function applySupersets(exercises: LoggedExercise[], slots: SessionSlot[]): void {
  for (const slot of slots) {
    if (!slot.supersetWith) continue;

    const firstIndex = exercises.findIndex((e) => e.slot === slot.slot);
    const partnerIndex = exercises.findIndex((e) => e.slot === slot.supersetWith);
    if (firstIndex === -1 || partnerIndex === -1) continue;
    // Only pair them if they actually ended up adjacent.
    if (Math.abs(firstIndex - partnerIndex) !== 1) continue;
    // Don't re-pair something already claimed by an earlier superset.
    if (exercises[firstIndex]!.supersetGroup || exercises[partnerIndex]!.supersetGroup) continue;

    const group = `ss-${slot.slot}`;
    exercises[firstIndex]!.supersetGroup = group;
    exercises[partnerIndex]!.supersetGroup = group;
  }
}

function resolveSets(slotSpec: SessionSlot, exercise: Exercise, block: BlockInfo): number {
  const base = slotSpec.sets ?? exercise.defaultSets;
  const scaled = Math.round(base * block.volumeMultiplier);
  return Math.max(slotSpec.optional ? 1 : 2, scaled);
}

/**
 * Anchor slots: whatever you used for this slot earlier in the block, so the
 * weight on the bar means something week to week. Falls back to the best
 * available option when the block is new.
 */
function pickAnchor(
  slot: SlotTag,
  candidates: Exercise[],
  thisBlock: WorkoutSession[],
  favorites: Set<string>,
  history: WorkoutSession[],
): Exercise {
  for (const session of thisBlock) {
    const prior = session.exercises.find((e) => e.slot === slot);
    const match = prior && candidates.find((c) => c.id === prior.exerciseId);
    if (match) return match;
  }
  return bestBy(candidates, (e) => {
    let score = e.quality;
    if (favorites.has(e.id)) score += 3;
    // Prefer something you have actually loaded before — history beats novelty
    // for a lift you are about to run for four straight weeks.
    if (hasHistory(history, e.id)) score += 1;
    return score;
  });
}

/**
 * Accessory slots: same scoring, but heavily penalise anything you did in the
 * last few sessions of this template so the rotation moves by itself.
 */
function pickRotating(
  slot: SlotTag,
  candidates: Exercise[],
  history: WorkoutSession[],
  favorites: Set<string>,
  goal: Goal,
): Exercise {
  const recent = history.slice(0, ROTATION_LOOKBACK);

  return bestBy(candidates, (e) => {
    let score = e.quality;
    if (favorites.has(e.id)) score += 2;

    const usedIndex = recent.findIndex((s) =>
      s.exercises.some((le) => le.slot === slot && le.exerciseId === e.id),
    );
    if (usedIndex === 0) score -= 6;
    else if (usedIndex === 1) score -= 3;
    else if (usedIndex === 2) score -= 1;

    if (goal === 'strength' && e.kind === 'compound') score += 1;
    if (goal === 'fat_loss' && e.loadType === 'timed') score += 1;

    return score;
  });
}

function hasHistory(workouts: WorkoutSession[], exerciseId: string): boolean {
  return workouts.some((w) => w.exercises.some((e) => e.exerciseId === exerciseId));
}

/** Highest score wins; ties break on id so results never shuffle between runs. */
function bestBy(items: Exercise[], score: (e: Exercise) => number): Exercise {
  const sorted = [...items].sort((a, b) => {
    const diff = score(b) - score(a);
    return diff !== 0 ? diff : a.id.localeCompare(b.id);
  });
  // `candidates` is never empty at the call sites, but keep the compiler happy.
  return sorted[0] ?? items[0]!;
}

/**
 * Alternatives for the "this machine is taken" button: the exercise's own
 * listed variations first, then anything else that fills the same slot.
 */
export function alternativesFor(
  exerciseId: string,
  slot: SlotTag,
  equipment: Set<string>,
  excludeIds: string[] = [],
): Exercise[] {
  const source = getExercise(exerciseId);
  const taken = new Set([exerciseId, ...excludeIds]);
  const pool = exercisesForSlot(slot, equipment).filter((e) => !taken.has(e.id));

  const listed = (source?.variations ?? [])
    .map((id) => pool.find((e) => e.id === id))
    .filter((e): e is Exercise => Boolean(e));

  const rest = pool
    .filter((e) => !listed.includes(e))
    .sort((a, b) => b.quality - a.quality || a.id.localeCompare(b.id));

  return [...listed, ...rest];
}
